import React from 'react';
import Icon from '../../../components/AppIcon';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const UserStats = ({ stats }) => {
  const registrationData = [
    { month: 'Jan', users: 45 },
    { month: 'Feb', users: 52 },
    { month: 'Mar', users: 48 },
    { month: 'Apr', users: 61 },
    { month: 'May', users: 55 },
    { month: 'Jun', users: 67 }
  ];

  const subscriptionData = [
    { name: 'Free', value: 45, color: '#6B7280' },
    { name: 'Basic', value: 30, color: '#3B82F6' },
    { name: 'Pro', value: 20, color: '#8B5CF6' },
    { name: 'Enterprise', value: 5, color: '#4F46E5' }
  ];

  const StatCard = ({ title, value, change, icon, color = 'text-primary' }) => (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-muted-foreground">{title}</p>
          <p className="text-2xl font-bold text-foreground mt-1">{value}</p>
          {change && (
            <p className={`text-sm mt-1 flex items-center ${
              change?.startsWith('+') ? 'text-green-600' : 'text-red-600'
            }`}>
              <Icon 
                name={change?.startsWith('+') ? 'TrendingUp' : 'TrendingDown'} 
                size={14} 
                className="mr-1" 
              />
              {change}
            </p>
          )}
        </div>
        <div className={`p-3 rounded-full bg-muted ${color}`}>
          <Icon name={icon} size={24} />
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Users"
          value={stats?.totalUsers?.toLocaleString()}
          change="+12.5%"
          icon="Users"
          color="text-blue-600"
        />
        <StatCard
          title="New This Month"
          value={stats?.newThisMonth}
          change="+8.2%"
          icon="UserPlus"
          color="text-green-600"
        />
        <StatCard
          title="Active Users"
          value={stats?.activeUsers?.toLocaleString()}
          change="+5.1%"
          icon="Activity"
          color="text-purple-600"
        />
        <StatCard
          title="Churn Rate"
          value={`${stats?.churnRate}%`}
          change="-2.1%"
          icon="UserMinus"
          color="text-red-600"
        />
      </div>
      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Registration Trends */}
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">Registration Trends</h3>
            <Icon name="TrendingUp" size={20} className="text-muted-foreground" />
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={registrationData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis 
                  dataKey="month" 
                  stroke="#6B7280"
                  fontSize={12}
                />
                <YAxis 
                  stroke="#6B7280"
                  fontSize={12}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E2E8F0',
                    borderRadius: '6px'
                  }}
                />
                <Bar dataKey="users" fill="#3B82F6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Subscription Distribution */}
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">Subscription Distribution</h3>
            <Icon name="PieChart" size={20} className="text-muted-foreground" />
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={subscriptionData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {subscriptionData?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry?.color} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E2E8F0',
                    borderRadius: '6px'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-2 gap-4 mt-4">
            {subscriptionData?.map((item) => (
              <div key={item?.name} className="flex items-center space-x-2">
                <div 
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: item?.color }}
                />
                <span className="text-sm text-muted-foreground">{item?.name}</span>
                <span className="text-sm font-medium text-foreground">{item?.value}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Additional Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Icon name="Clock" size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Avg. Session Duration</p>
              <p className="text-lg font-semibold text-foreground">24m 32s</p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Icon name="Target" size={20} className="text-green-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Conversion Rate</p>
              <p className="text-lg font-semibold text-foreground">3.2%</p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Icon name="DollarSign" size={20} className="text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Revenue per User</p>
              <p className="text-lg font-semibold text-foreground">$42.50</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserStats;